import { Alpine } from '../../core/alpine-core.js';
import { apiFetch } from '../api.js';
import { fetchAdminGoogleMapsApiKey, loadGoogleMapsAdminSdk } from '../google-maps-sdk.js';
import { withLeagueId } from '../league-context.js';

function gmapsFromLL(v) {
    if (!v || v.lat == null || v.lng == null) return '#';
    return `https://www.google.com/maps/search/?api=1&query=${v.lat},${v.lng}`;
}
function gdirsFromLL(v) {
    if (!v || v.lat == null || v.lng == null) return '#';
    return `https://www.google.com/maps/dir/?api=1&destination=${v.lat}%2C${v.lng}`;
}

function isFixtureNotFoundError(status, message) {
    if (status === 404) return true;
    return /not found|fixture_not_found|\b404\b/i.test(String(message || ''));
}

Alpine.data('fixturesView', () => ({

    id: null,
    item: null,
    loading: true,
    error: '',
    fixtureNotFound: false,
    removeConfirmOpen: false,
    removeBusy: false,
    message: '',
    showLiveScoringSoonDialog: false,

    mapsBlocked: false,
    mapsBlockedReason: '',

    fixtureSession: null,
    sessionBusy: false,

    get canLiveScore() {
        return this.$store.features.enabled('liveFixtures')
            && (this.item?.type || 'fixture') === 'fixture'
            && !this.isCompleted();
    },

    get hasLiveSession() {
        return !!this.fixtureSession?._id;
    },

    async loadFixtureSessionMeta() {
        if (!this.item?._id || !this.canLiveScore) {
            this.fixtureSession = null;
            return;
        }
        try {
            const res = await apiFetch(`/fixtures/${this.item._id}/session`);
            const data = await res.json();
            if (res.ok && data?.ok) {
                this.fixtureSession = data.session || null;
            }
        } catch {
            this.fixtureSession = null;
        }
    },

    openFixtureScorer() {
        if (!this.item?._id) return;
        const router = window.PineconeRouter;
        const path = `/admin/fixtures/${this.item._id}/setup`;
        if (router?.navigate) router.navigate(path);
        else location.assign(path);
    },

    /** Admins go straight to live scoring; everyone else sees the scoresheet-first dialog. */
    showLiveScoringComingSoon() {
        if (this.$store?.session?.hasRole?.('admin')) {
            this.openFixtureScorer();
            return;
        }
        this.showLiveScoringSoonDialog = true;
    },

    hideLiveScoringComingSoon() {
        this.showLiveScoringSoonDialog = false;
    },

    openScoresheetFromComingSoon() {
        if (!this.item?._id) return;
        this.hideLiveScoringComingSoon();
        const router = window.PineconeRouter;
        const path = this.hasSheet
            ? `/admin/scoresheet/${this.sheetMeta.id}/edit`
            : `/admin/scoresheet/new?fx=${this.item._id}`;
        if (router?.navigate) router.navigate(path);
        else location.assign(path);
    },

    openFixtureScorerWindow() {
        if (!this.item?._id) return;
        if (!this.$store?.session?.hasRole?.('admin')) {
            this.showLiveScoringSoonDialog = true;
            return;
        }
        const path = `/admin/fixtures/${this.item._id}/scorer`;
        window.open(path, `pdl-fixture-scorer-${this.item._id}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
    },

    openFixtureViewer() {
        if (!this.item?._id) return;
        const router = window.PineconeRouter;
        const path = `/admin/fixtures/${this.item._id}/live`;
        if (router?.navigate) router.navigate(path);
        else location.assign(path);
    },

    openFixtureBoard() {
        if (!this.item?._id) return;
        const path = `/admin/fixtures/${this.item._id}/board`;
        window.open(path, `pdl-fixture-board-${this.item._id}`, 'popup=yes,width=1600,height=900,resizable=yes,scrollbars=yes');
    },

    openFixtureBoardDebug() {
        if (!this.item?._id) return;
        const path = `/admin/fixtures/${this.item._id}/board?debug=1`;
        window.open(path, `pdl-fixture-board-debug-${this.item._id}`, 'popup=yes,width=1440,height=980,resizable=yes,scrollbars=yes');
    },

    openFixtureSummary() {
        if (!this.item?._id) return;
        const router = window.PineconeRouter;
        const path = `/admin/fixtures/${this.item._id}/summary`;
        if (router?.navigate) router.navigate(path);
        else location.assign(path);
    },

    fixtureBoardUrl() {
        if (!this.item?._id) return '';
        return `${window.location.origin}/admin/fixtures/${this.item._id}/board`;
    },

    async castFixtureBoard() {
        const boardUrl = this.fixtureBoardUrl();
        if (!boardUrl) return;
        try {
            if ('PresentationRequest' in window) {
                const request = new window.PresentationRequest([boardUrl]);
                await request.start();
                window.Alpine?.store?.('toastr')?.success?.('Board cast started', 2500);
                return;
            }
        } catch {
        }

        try {
            if (navigator.share) {
                await navigator.share({
                    title: `${this.item?.homeTeamName || 'Home'} vs ${this.item?.awayTeamName || 'Away'} board`,
                    text: 'Open the live fixture board',
                    url: boardUrl,
                });
                window.Alpine?.store?.('toastr')?.success?.('Board link shared', 2500);
                return;
            }
        } catch (error) {
            if (error?.name === 'AbortError') return;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(boardUrl);
                this.openFixtureBoard();
                window.Alpine?.store?.('toastr')?.success?.('Board opened and link copied', 3000);
                return;
            }
        } catch {
        }

        this.openFixtureBoard();
        window.Alpine?.store?.('toastr')?.success?.('Board opened in a new window', 2500);
    },

    get canVerifyFixtureSession() {
        const isAdmin = window.Alpine?.store?.('session')?.me?.role === 'admin';
        const homeConfirmed = !!this.fixtureSession?.confirmations?.home?.confirmedAt;
        const awayConfirmed = !!this.fixtureSession?.confirmations?.away?.confirmedAt;
        return !!this.fixtureSession?._id
            && String(this.fixtureSession?.status || '') === 'submitted'
            && isAdmin
            && homeConfirmed
            && awayConfirmed;
    },

    get myFixtureTeamSide() {
        const teamId = String(window.Alpine?.store?.('session')?.me?.registeredTeamId || '');
        if (!teamId || !this.item) return '';
        if (teamId === String(this.item.homeTeamId || this.item.homeTeamId?._id || '')) return 'home';
        if (teamId === String(this.item.awayTeamId || this.item.awayTeamId?._id || '')) return 'away';
        return '';
    },

    get canCaptainConfirmFixtureSession() {
        const role = window.Alpine?.store?.('session')?.me?.role || '';
        const side = this.myFixtureTeamSide;
        if (!this.fixtureSession?._id || !side) return false;
        if (!['captain', 'admin'].includes(String(role))) return false;
        return ['submitted', 'completed', 'verified'].includes(String(this.fixtureSession?.status || ''))
            && !this.fixtureSession?.confirmations?.[side]?.confirmedAt;
    },

    async verifyFixtureSession() {
        if (!this.fixtureSession?._id) return;
        this.sessionBusy = true;
        try {
            const res = await apiFetch(`/fixture-sessions/${this.fixtureSession._id}/verify`, { method: 'POST', body: {} });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to verify fixture session');
            this.fixtureSession = data.session;
            this.message = 'Fixture session verified.';
            window.Alpine?.store?.('toastr')?.success?.('Fixture session verified', 2500);
        } catch (e) {
            window.Alpine?.store?.('toastr')?.error?.(e.message || 'Unable to verify fixture session', 3000);
        } finally {
            this.sessionBusy = false;
        }
    },

    async confirmFixtureSession() {
        if (!this.fixtureSession?._id || !this.myFixtureTeamSide) return;
        this.sessionBusy = true;
        try {
            const res = await apiFetch(`/fixture-sessions/${this.fixtureSession._id}/confirm`, {
                method: 'POST',
                body: { side: this.myFixtureTeamSide },
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Failed to confirm fixture session');
            this.fixtureSession = data.session;
            window.Alpine?.store?.('toastr')?.success?.('Captain confirmation saved', 2500);
        } catch (e) {
            window.Alpine?.store?.('toastr')?.error?.(e.message || 'Unable to confirm fixture session', 3000);
        } finally {
            this.sessionBusy = false;
        }
    },

    sheetMeta: { exists: false, id: '', status: '' }, // status: 'draft' | 'submitted' | 'approved'

    map: null,
    marker: null,

    placeholderLogo: '/assets/placeholder-player.png', // fallback image

    sheet: null,
    breakdown: {
        singles: [],
        doubles: [],
        beer: { winner: '', home: [], away: [] },
        totals: { singles: { home: 0, away: 0 }, doubles: { home: 0, away: 0 }, beer: [0, 0], match: [0, 0] }
    },

    // ---- SAFE GETTERS for template (avoid accessing undefined paths)
    get homeRoster() { return this.item?.home?.players?.roster ?? []; },
    get homeReserves() { return this.item?.home?.players?.reserves ?? []; },
    get awayRoster() { return this.item?.away?.players?.roster ?? []; },
    get awayReserves() { return this.item?.away?.players?.reserves ?? []; },

    get hasLatLng() {
        return !!(this.item?.venue && typeof this.item.venue.lat === 'number' && typeof this.item.venue.lng === 'number');
    },

    // ---- Derived helpers for buttons
    get hasSheet() { return this.sheetMeta.exists && !!this.sheetMeta.id; },
    get isDraft() { return this.sheetMeta.status === 'draft'; },
    get isSubmitted() { return this.sheetMeta.status === 'submitted'; },
    get isApproved() { return this.sheetMeta.status === 'approved'; },
    get isPendingReview() {
        const st = String(this.item?.status || '').toLowerCase();
        return st === 'pending_review' || (this.hasSheet && this.isSubmitted);
    },

    reviewSheet: null,
    reviewBusy: false,
    reviewRejectReason: '',

    borrowedPlayerLabel(meta) {
        if (!meta?.borrowed) return '';
        const from = String(meta.borrowedFromTeamName || 'another team').trim();
        return `Borrowed from ${from}`;
    },

    async loadReviewSheet(fixtureId) {
        const id = String(fixtureId || this.item?._id || '').trim();
        if (!id) return;
        try {
            const res = await apiFetch(`/scoresheets/by-fixture/${encodeURIComponent(id)}`);
            if (!res.ok) {
                this.reviewSheet = null;
                return;
            }
            const j = await res.json();
            this.reviewSheet = j?.sheet || null;
        } catch {
            this.reviewSheet = null;
        }
    },

    async approveFixtureReview() {
        if (!this.item?._id || this.reviewBusy) return;
        this.reviewBusy = true;
        try {
            const res = await apiFetch(`/fixtures/${encodeURIComponent(this.item._id)}/approve-result`, {
                method: 'PATCH',
                body: {},
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Approve failed');
            window.Alpine?.store?.('toastr')?.success?.('Fixture approved — stats updated.', 3500);
            await this.load(this.item._id);
            await this.loadSheetMeta(this.item._id);
            await this.loadReviewSheet(this.item._id);
            if (this.isCompleted() && this.isApproved) await this.loadSheetFull(this.item._id);
        } catch (e) {
            window.Alpine?.store?.('toastr')?.error?.(e.message || 'Unable to approve fixture', 4000);
        } finally {
            this.reviewBusy = false;
        }
    },

    async rejectFixtureReview() {
        if (!this.item?._id || this.reviewBusy) return;
        this.reviewBusy = true;
        try {
            const res = await apiFetch(`/fixtures/${encodeURIComponent(this.item._id)}/reject-result`, {
                method: 'PATCH',
                body: { reason: String(this.reviewRejectReason || '').trim() },
            });
            const data = await res.json();
            if (!res.ok || !data?.ok) throw new Error(data?.message || 'Reject failed');
            window.Alpine?.store?.('toastr')?.info?.('Fixture returned for corrections.', 3500);
            await this.load(this.item._id);
            await this.loadSheetMeta(this.item._id);
            await this.loadReviewSheet(this.item._id);
        } catch (e) {
            window.Alpine?.store?.('toastr')?.error?.(e.message || 'Unable to reject fixture', 4000);
        } finally {
            this.reviewBusy = false;
        }
    },

    gmapsFromLL, gdirsFromLL,

    mapsBlockedTitle() {
        if (this.mapsBlockedReason === 'invalid') return 'Google Maps key rejected';
        if (this.mapsBlockedReason === 'load_failed') return 'Google Maps could not load';
        return 'Google Maps API key not configured';
    },

    mapsBlockedDetail() {
        if (this.mapsBlockedReason === 'invalid') {
            return 'The key was refused by Google. Check billing and HTTP referrer restrictions in Google Cloud Console.';
        }
        if (this.mapsBlockedReason === 'load_failed') {
            return 'The map script failed to load or timed out.';
        }
        return 'Configure a Maps JavaScript API key in Account settings → Subscription (Google Maps API).';
    },

    readRouteId() {
        const path = location.pathname;
        const parts = path.split('/').filter(Boolean);
        const idx = parts.indexOf('fixtures');
        if (idx >= 0 && parts[idx + 1]) return decodeURIComponent(parts[idx + 1]);
        return null;
    },

    listHref() {
        return withLeagueId('/admin/fixtures');
    },

    confirmRemoveStale() {
        this.removeConfirmOpen = true;
    },

    async removeStaleFixture() {
        const id = String(this.id || this.readRouteId() || '').trim();
        if (!id || this.removeBusy) return;
        this.removeBusy = true;
        try {
            const res = await apiFetch(`/fixtures/${encodeURIComponent(id)}`, { method: 'DELETE' });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(data?.message || data?.error || 'Remove failed');
            }
            this.$store.toastr.success('Fixture removed');
            this.removeConfirmOpen = false;
            location.href = this.listHref();
        } catch (e) {
            this.$store.toastr.error(e.message || 'Remove failed');
            this.removeConfirmOpen = false;
        } finally {
            this.removeBusy = false;
        }
    },

    getStats(side, playerId) {
        const bag = side === 'home'
            ? (this.item?.home?.statsById || {})
            : (this.item?.away?.statsById || {});
        const key = String(playerId);
        return bag[key] || { apps: 0, legsFor: 0, legsAgainst: 0, tons: 0, closes: 0, winPct: 0 };
    },
    displayName(p) { return p.fullName || 'Player'; },
    displayNickname(p) { return p.nickname || ''; },
    goToPlayer(p) {
        if (!p) return;
        location.href = `/admin/players/${p._id}`;
    },

    async init() {
        this.loading = true;
        this.fixtureNotFound = false;
        try {
            const id = this.readRouteId();
            if (!id) throw new Error('Missing fixture id');
            this.id = id;
            await this.load(id);
            await this.loadSheetMeta(id);
            await this.loadFixtureSessionMeta();
            if (this.isPendingReview) {
                await this.loadReviewSheet(id);
            }
            // Only fetch + show stats if the fixture is completed AND the sheet is approved
            if (this.isCompleted() && this.isApproved) {
                await this.loadSheetFull(id);
            }
            this.mapsBlocked = false;
            this.mapsBlockedReason = '';
            if (this.hasLatLng) {
                let apiKey = '';
                try {
                    apiKey = await fetchAdminGoogleMapsApiKey();
                } catch {
                    apiKey = '';
                }
                if (!apiKey) {
                    this.mapsBlocked = true;
                    this.mapsBlockedReason = 'no_key';
                } else {
                    try {
                        await loadGoogleMapsAdminSdk(apiKey, { libraries: '' });
                        this.mapsBlocked = false;
                        this.mapsBlockedReason = '';
                    } catch (e) {
                        this.mapsBlocked = true;
                        const code = e?.code || (e?.message === 'auth_failed' ? 'AUTH_FAILED' : '');
                        this.mapsBlockedReason = code === 'AUTH_FAILED' || e?.message === 'auth_failed' ? 'invalid' : 'load_failed';
                    }
                }
            }
            this.$nextTick(() => {
                if (this.hasLatLng && !this.mapsBlocked) this.initMap();
            });
        } catch (e) {
            console.error(e);
            this.error = e.message || 'Unable to initialize fixture view';
        } finally {
            this.loading = false;
        }
    },

    async load(id) {
        this.id = id;
        try {
            const res = await apiFetch(`/fixtures/${id}?populate=true`);
            const data = await res.json();
            if (!res.ok || !data?.ok) {
                const msg = data?.message || data?.error || 'Failed to load fixture';
                if (isFixtureNotFoundError(res.status, msg)) {
                    this.fixtureNotFound = true;
                    this.item = null;
                    this.error = 'This fixture could not be found. It may have been deleted or the link is out of date.';
                    return;
                }
                throw new Error(msg);
            }
            this.fixtureNotFound = false;
            this.item = data.fixture;
            this.item.legsHome ?? 0;
            this.item.legsAway ?? 0;
            this.error = '';
        } catch (e) {
            console.error(e);
            const msg = e.message || 'Unable to load fixture';
            if (isFixtureNotFoundError(null, msg)) {
                this.fixtureNotFound = true;
                this.item = null;
                this.error = 'This fixture could not be found. It may have been deleted or the link is out of date.';
            } else {
                this.error = msg;
            }
        }
    },

    async loadSheetMeta(fixtureId) {
        try {
            const res = await apiFetch(`/scoresheets/by-fixture/${fixtureId}`); // you already use this route elsewhere
            if (!res.ok) {
                this.sheetMeta = { exists: false, id: '', status: '' };
                return;
            }
            const j = await res.json();
            const s = j.sheet || {};
            this.sheetMeta = { exists: !!s._id, id: String(s._id || ''), status: String(s.status || 'draft') };
        } catch {
            this.sheetMeta = { exists: false, id: '', status: '' };
        }
    },

    formatDate(dt) {
        if (!dt) return 'Date TBC';
        const d = new Date(dt);
        return d.toLocaleDateString('en-GB', {
            weekday: 'long',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
        }) + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    },

    mapFor(venue) {
        if (!venue) return 'https://www.google.com/maps';
        return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue + ' Phuket')}`;
    },

    initMap() {
        if (!this.hasLatLng) return; // nothing to do
        if (this.mapsBlocked) return;
        if (!(window.google && window.google.maps)) {
            const t = setInterval(() => {
                if (window.google && window.google.maps) {
                    clearInterval(t); this.initMap();
                }
            }, 150);
            return;
        }
        const el = document.getElementById('venue-map');
        if (!el) return;

        const center = {
            lat: Number(this.item.venue.lat),
            lng: Number(this.item.venue.lng)
        };
        this.map = new google.maps.Map(el, {
            center, zoom: 16, mapTypeControl: false, streetViewControl: false, fullscreenControl: true
        });
        this.marker = new google.maps.Marker({ position: center, map: this.map });
    },

    winnerText() {
        const h = +this.form.legsHome || 0;
        const a = +this.form.legsAway || 0;
        if (h === a) return 'Currently a draw';
        return h > a ? `${this.item?.homeTeamName || 'Home'} ahead` : `${this.item?.awayTeamName || 'Away'} ahead`;
    },

    isCompleted() {
        if (String(this.item?.status || '').toLowerCase() === 'pending_review') return false;
        if (this.item?.status === 'completed') return true;
        const h = this.item?.legsHome, a = this.item?.legsAway;
        return Number.isFinite(h) && Number.isFinite(a) && String(this.item?.status || '') === 'completed';
    },
    homeWin() {
        const h = this.item?.legsHome ?? 0, a = this.item?.legsAway ?? 0;
        return this.isCompleted() && h > a;
    },
    awayWin() {
        const h = this.item?.legsHome ?? 0, a = this.item?.legsAway ?? 0;
        return this.isCompleted() && a > h;
    },
    buildFixtureStats(sheet) {
        // Returns { home: {statsById}, away: {statsById} }
        const S = () => ({ apps: 0, legsFor: 0, legsAgainst: 0, tons: 0, closes: 0, winPct: 0 });
        const sideMaps = { home: Object.create(null), away: Object.create(null) };
        const add = (side, playerId, inc) => {
            if (!playerId) return;
            const pid = String(playerId);
            const bag = sideMaps[side];
            bag[pid] ||= S();
            const s = bag[pid];
            if (inc.apps) s.apps += inc.apps;
            if (inc.legsFor) s.legsFor += inc.legsFor;
            if (inc.legsAgainst) s.legsAgainst += inc.legsAgainst;
            if (inc.tons) s.tons += inc.tons;
            if (inc.closes) s.closes += inc.closes;
        };

        // 1) Singles
        for (const m of sheet.singles || []) {
            const hId = m?.home?.playerId, aId = m?.away?.playerId;
            const hLegs = Number(m?.home?.legs) || 0;
            const aLegs = Number(m?.away?.legs) || 0;
            add('home', hId, { apps: hId ? 1 : 0, legsFor: hLegs, legsAgainst: aLegs, tons: (m?.home?.tons || []).length, closes: (m?.home?.closes || []).length });
            add('away', aId, { apps: aId ? 1 : 0, legsFor: aLegs, legsAgainst: hLegs, tons: (m?.away?.tons || []).length, closes: (m?.away?.closes || []).length });
        }

        // 2) Doubles (apps +1 for each player on that side)
        for (const d of sheet.doubles || []) {
            const hLegs = Number(d?.home?.legs) || 0;
            const aLegs = Number(d?.away?.legs) || 0;

            for (const p of d?.home?.players || []) {
                add('home', p?.playerId, {
                    apps: p?.playerId ? 1 : 0,
                    legsFor: hLegs / 2,
                    legsAgainst: aLegs,
                    tons: (p?.tons || []).length,
                    closes: (p?.closes || []).length,
                });
            }
            for (const p of d?.away?.players || []) {
                add('away', p?.playerId, {
                    apps: p?.playerId ? 1 : 0,
                    legsFor: aLegs / 2,
                    legsAgainst: hLegs,
                    tons: (p?.tons || []).length,
                    closes: (p?.closes || []).length,
                });
            }
        }

        // 3) Beer leg: count tons/closes if you want them visible; do NOT add apps/legs
        // (If you’d prefer to exclude beer completely, comment this block out.)
        for (const side of ['home', 'away']) {
            for (const p of (sheet?.beer?.[side] || [])) {
                add(side, p?.playerId, {
                    tons: (p?.tons || []).length,
                    closes: (p?.closes || []).length,
                });
            }
        }

        // 4) Compute winPct
        for (const side of ['home', 'away']) {
            const bag = sideMaps[side];
            for (const pid in bag) {
                const s = bag[pid];
                const denom = s.legsFor + s.legsAgainst;
                s.winPct = denom ? Math.round((s.legsFor / denom) * 100) : 0;
            }
        }

        return { home: sideMaps.home, away: sideMaps.away };
    },

    async loadFixtureStats(fixtureId) {
        try {
            const res = await apiFetch(`/scoresheets/by-fixture/${fixtureId}`);
            if (!res.ok) return;
            const { sheet } = await res.json();
            if (!sheet || sheet.status !== 'approved') return; // only when approved

            const { home, away } = this.buildFixtureStats(sheet);

            // Attach to your existing shape so getStats() can read them
            this.item = this.item || {};
            this.item.home = this.item.home || {};
            this.item.away = this.item.away || {};
            this.item.home.statsById = home;
            this.item.away.statsById = away;

            // Repaint
            this.$nextTick(() => { });
        } catch (e) {
            console.warn('loadFixtureStats skipped:', e);
        }
    },

    // Helper: quick name/label (search both teams' roster+reserves)
    nameFor(playerId) {
        if (!playerId) return '—';
        const id = String(playerId);
        const pools = [
            ...this.homeRoster, ...this.homeReserves,
            ...this.awayRoster, ...this.awayReserves
        ];
        const p = pools.find(x => String(x._id) === id);
        if (!p) return id.slice(-6); // fallback: last 6 chars
        // return p.nickname ? `${p.fullName} (${p.nickname})` : p.fullName || '—';
        return p.fullName ? p.fullName : '-';
    },

    // Build pretty rows from raw sheet
    buildBreakdown(sheet) {
        const singles = (sheet.singles || []).map((m, idx) => {
            const h = m?.home || {}, a = m?.away || {};
            const hLegs = Number(h.legs) || 0, aLegs = Number(a.legs) || 0;
            const winner = hLegs === aLegs ? 'draw' : (hLegs > aLegs ? 'home' : 'away');
            return {
                no: idx + 1,
                home: {
                    id: h.playerId, name: this.nameFor(h.playerId),
                    legs: hLegs, tons: (h.tons || []), closes: (h.closes || [])
                },
                away: {
                    id: a.playerId, name: this.nameFor(a.playerId),
                    legs: aLegs, tons: (a.tons || []), closes: (a.closes || [])
                },
                winner
            };
        });

        const doubles = (sheet.doubles || []).map((d, idx) => {
            const hp = (d?.home?.players || []).map(p => ({ id: p.playerId, name: this.nameFor(p.playerId), tons: (p.tons || []), closes: (p.closes || []) }));
            const ap = (d?.away?.players || []).map(p => ({ id: p.playerId, name: this.nameFor(p.playerId), tons: (p.tons || []), closes: (p.closes || []) }));
            const hLegs = Number(d?.home?.legs) || 0, aLegs = Number(d?.away?.legs) || 0;
            const winner = hLegs === aLegs ? 'draw' : (hLegs > aLegs ? 'home' : 'away');
            return {
                no: idx + 1,
                home: { legs: hLegs, players: hp },
                away: { legs: aLegs, players: ap },
                winner
            };
        });

        const beer = {
            winner: sheet?.beer?.winner || '',
            home: (sheet?.beer?.home || []).map(p => ({ id: p.playerId, name: this.nameFor(p.playerId), tons: (p.tons || []), closes: (p.closes || []) })),
            away: (sheet?.beer?.away || []).map(p => ({ id: p.playerId, name: this.nameFor(p.playerId), tons: (p.tons || []), closes: (p.closes || []) })),
        };

        const totals = {
            singles: sheet?.totals?.singles || { home: 0, away: 0 },
            doubles: sheet?.totals?.doubles || { home: 0, away: 0 },
            beer: sheet?.totals?.beer || [0, 0],
            match: sheet?.totals?.match || [0, 0]
        };

        this.breakdown = { singles, doubles, beer, totals };
        // console.log(this.breakdown);
    },

    // Load full sheet and build breakdown (also leaves your per-player stats intact)
    async loadSheetFull(fixtureId) {
        try {
            const res = await apiFetch(`/scoresheets/by-fixture/${fixtureId}`);
            if (!res.ok) return;
            const { sheet } = await res.json();
            if (!sheet || sheet.status !== 'approved') return;
            this.sheet = sheet;
            this.buildBreakdown(sheet);
        } catch (e) {
            console.warn('loadSheetFull failed:', e);
        }
    },

}));
